import { FiAlertTriangle } from "react-icons/fi";
import { IApprovalHistorySummary } from "@/common/types";
import { SummaryWrapper } from "../style";
import { formatShortDate } from "../format";

export interface IHistorySummaryProps {
  summary: IApprovalHistorySummary;
}

/**
 * MED-653 — the counts an admin judges a change pattern by, shown above the
 * timeline. `headline` is composed by the backend so the panel, the queue and
 * any future export word it identically.
 */
export function HistorySummary({ summary }: IHistorySummaryProps) {
  const tiles = [
    { label: "Approved", value: summary.approved_count },
    { label: "Edited and resubmitted", value: summary.resubmission_count },
    { label: "Declined", value: summary.declined_count },
    { label: "Queried", value: summary.queried_count },
  ];

  // Nothing emits an inventory purge yet (MED-651 owns purge-on-edit), so the
  // tile stays hidden until a product actually has one.
  if (summary.inventory_purge_count > 0) {
    tiles.push({
      label: "Inventory purges",
      value: summary.inventory_purge_count,
    });
  }

  return (
    <SummaryWrapper>
      <h3 className="headline">{summary.headline}</h3>

      {summary.frequent_resubmission ? (
        <div className="flag">
          <FiAlertTriangle className="flag-icon" />
          <p className="flag-text">
            Flagged for closer scrutiny: this product has been edited and
            resubmitted {summary.resubmission_count} times, at or above the
            review threshold of {summary.frequent_resubmission_threshold}. Check
            the change pattern below before approving.
          </p>
        </div>
      ) : null}

      <div className="counts">
        {tiles.map((tile) => (
          <div className="count-tile" key={tile.label}>
            <span className="count-value">{tile.value}</span>
            <span className="count-label">{tile.label}</span>
          </div>
        ))}
      </div>

      {summary.first_submitted_at ? (
        <div className="last-decision">
          <span className="decision-label">First submitted:</span>
          <span>{formatShortDate(summary.first_submitted_at)}</span>
        </div>
      ) : null}

      {summary.last_decision ? (
        <div className="last-decision">
          <span className="decision-label">Last decision:</span>
          <span>
            {summary.last_decision.label} on{" "}
            {formatShortDate(summary.last_decision.occurred_at)}
            {summary.last_decision.decided_by
              ? ` by ${summary.last_decision.decided_by}`
              : ""}
            {summary.last_decision.decided_by_role
              ? ` (${summary.last_decision.decided_by_role})`
              : ""}
          </span>
        </div>
      ) : null}
    </SummaryWrapper>
  );
}
